import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  BoardMeetingPatchPaths,
  BoardMeetingProccessingState,
  IBoardDetailMeeting,
  IBoardMeetingParticipant,
  IBoardMeetingParticipantForEdit,
  IBoardMeetingPatchWrapper,
  MeetingStatusEnum,
} from '../../models/BoardMeeting'
import { MeetingNoticeInviteReply } from '../../models/MeetingNoticeInviteReply'

const initialState = {
  data: null as IBoardDetailMeeting | null,
  error: null as string | null,
  loading: false,
  currentlyPatching: null as BoardMeetingPatchPaths[] | null,
  processing: null as BoardMeetingProccessingState | null,
  initiallySaved: false,

  participantsForEdit: {
    data: null as IBoardMeetingParticipantForEdit[] | null,
    loading: false,
    error: null as string | null,
  },

  participantUpdate: {
    error: null as null | string,
  },

  invited: {
    loading: false as boolean,
    loadingAll: false as boolean,
    error: null as string | null,

    // if array, sending specific ids, if boolean, its "sending all"
    isSending: null as boolean | string[] | null,
  },
}

export const lastPersonSigned = createAction<string>(
  'meeting/last-person-signed'
)

export const signingCompleted = createAction<string>(
  'meeting/signing-completed'
)

const meetingSlice = createSlice({
  name: 'meeting',
  initialState,
  reducers: {
    fetchMeetingRequest: (state, _: PayloadAction<string>) => {
      state.loading = true
      state.data = null
      state.error = null
      state.processing = null
    },

    fetchMeetingSilentRequest: (_state, _: PayloadAction<string>) => {},
    fetchMeetingSilentSuccess: (
      state,
      action: PayloadAction<IBoardDetailMeeting>
    ) => {
      state.data = action.payload
    },
    fetchMeetingParticipantsSilentRequest: (
      _state,
      _: PayloadAction<string>
    ) => {},
    fetchMeetingParticipantsSilentSuccess: (
      state,
      action: PayloadAction<IBoardMeetingParticipant[]>
    ) => {
      if (state.data) {
        state.data.participants = action.payload
      }
    },

    fetchMeetingSuccess: (
      state,
      action: PayloadAction<IBoardDetailMeeting>
    ) => {
      state.loading = false
      state.error = null
      state.data = action.payload
    },
    fetchMeetingError: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    meetingReset: (state) => {
      state.data = null
      state.participantUpdate.error = null
    },
    patchMeetingRequest: (
      state,
      action: PayloadAction<IBoardMeetingPatchWrapper>
    ) => {
      state.currentlyPatching = action.payload.models.map(
        (x) => x.path as BoardMeetingPatchPaths
      )

      state.processing = BoardMeetingProccessingState.MeetingUpdating
    },
    patchMeetingSuccess: (state) => {
      state.currentlyPatching = null
      state.processing = BoardMeetingProccessingState.MeetingUpdated
    },
    patchMeetingError: (state, action: PayloadAction<string>) => {
      state.processing = BoardMeetingProccessingState.MeetingUpdateError
      state.error = action.payload
    },

    deleteMeetingRequest: (state, _: PayloadAction<string>) => {
      state.processing = BoardMeetingProccessingState.Deleting
    },
    deleteMeetingSuccess: (state) => {
      state.data = null
      state.processing = BoardMeetingProccessingState.Deleted
    },
    deleteMeetingError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.processing = BoardMeetingProccessingState.DeleteError
    },
    resetState: (state) => {
      state.processing = null
      state.data = null
      state.loading = false
      state.error = null
    },
    resetProcessing: (state) => {
      state.processing = null
    },

    setParticipantsInitiallySaved: (state, action: PayloadAction<boolean>) => {
      state.initiallySaved = action.payload
    },

    sendParticipantsNoticeRequest: (state, _: PayloadAction<string[]>) => {
      state.invited.loading = true
      state.invited.error = null
    },
    sendParticipantsNoticeSuccess: (state) => {
      state.invited.loading = false
      state.invited.loadingAll = false
    },
    sendParticipantsNoticeError: (state, action: PayloadAction<string>) => {
      state.invited.loading = false
      state.invited.error = action.payload
    },

    sendParticipantNoticeToAllRequest: (state) => {
      state.invited.loadingAll = true
      state.invited.error = null
    },

    updateMeetingStatusRequested: (
      state,
      _action: PayloadAction<MeetingStatusEnum>
    ) => {
      state.processing = BoardMeetingProccessingState.MeetingStatusUpdating
    },

    updateMeetingStatusSuccess: (
      state,
      action: PayloadAction<MeetingStatusEnum>
    ) => {
      if (state.data) {
        state.data.status = action.payload
      }

      state.processing = null
    },

    updateInvitedSendingStatus: (
      state,
      { payload }: PayloadAction<string[] | boolean | null>
    ) => {
      state.invited.isSending = payload
    },

    updateMeetingResponsibleRequested: (state) => {
      state.processing =
        BoardMeetingProccessingState.MeetingUpdateResponsiblePerson
    },
    updateMeetingResponsibleSuccess: (state) => {
      state.processing = null
    },

    noticeEmailSent: (
      _,
      _action: PayloadAction<MeetingNoticeInviteReply | null>
    ) => {},

    fetchMeetingParticipantsForEditRequest: (state) => {
      state.participantsForEdit.loading = true
      state.participantsForEdit.error = null
    },

    fetchMeetingParticipantsForEditSuccess: (
      state,
      action: PayloadAction<IBoardMeetingParticipantForEdit[]>
    ) => {
      state.participantsForEdit.loading = false
      state.participantsForEdit.data = action.payload
    },

    fetchMeetingParticipantsForEditError: (
      state,
      action: PayloadAction<string>
    ) => {
      state.participantsForEdit.loading = false
      state.participantsForEdit.error = action.payload
    },

    meetingParticipantsForEditReset: (state) => {
      state.participantsForEdit.data = null
      state.participantsForEdit.error = null
    },
  },
})

export default meetingSlice
